import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";

const canneseriesEn: ChauffeurPage = {
  slug: "canneseries",
  title: "Private chauffeur CANNESERIES – series festival VTC",
  subtitle:
    "Private chauffeur during CANNESERIES in Cannes: pink-carpet drop-offs, Nice airport meet-and-greet and daily hire around the Palais des Festivals.",
  keywords: [
    "canneseries chauffeur",
    "canneseries vtc",
    "cannes series festival chauffeur",
  ],
  hero: {
    title: "Private chauffeur during CANNESERIES",
    subtitle:
      "The international series festival in April: pink carpet, screenings and parties with a dedicated chauffeur around the Palais des Festivals.",
    keywords: [
      "CANNESERIES chauffeur",
      "series festival VTC",
      "Palais des Festivals chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Private chauffeur during CANNESERIES",
    badges: ["VIP airport welcome", "Pink-carpet drop-off", "Late-night runs"],
  },
  benefits: {
    heading: "Why a chauffeur during CANNESERIES",
    items: [
      {
        title: "Pink carpet and Palais",
        description:
          "Around the screenings, the approaches to the Palais des Festivals get busy. The chauffeur drops you as close as possible within the perimeter and pink-carpet timing.",
      },
      {
        title: "Nice airport welcome",
        description:
          "In April, talent, press and guests arrive through Nice Côte d'Azur. Name-board welcome, flight tracking and a direct transfer to the hotel.",
      },
      {
        title: "Talent, press, industry",
        description:
          "Separate flows for actors, press teams and series professionals: punctual before screenings and panels.",
      },
      {
        title: "Late-night runs secured",
        description:
          "Evening screenings and festival parties: late return with a chauffeur who waits.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services during CANNESERIES",
    services: [
      {
        title: "Nice airport meet-and-greet",
        text: "Nice ↔ Cannes transfer with flight tracking during the series festival.",
      },
      {
        title: "Pink-carpet drop-off",
        text: "Discreet approach as close as possible to the Palais des Festivals, within the perimeter and screening timing.",
      },
      {
        title: "Full-day hire",
        text: "A dedicated chauffeur by the hour or day to chain screenings, panels and meetings.",
      },
      {
        title: "Evenings & festival parties",
        text: "Croisette hotels, private beaches and dinners, with a guaranteed late-night return.",
      },
      {
        title: "Team & press shuttles",
        text: "Multi-vehicle coordination for delegations, press officers and partners.",
      },
    ],
  },
  locations: {
    heading: "Key points during CANNESERIES",
    items: [
      {
        name: "Palais des Festivals et des Congrès",
        description: "Pink carpet, screening rooms and meeting spaces.",
      },
      {
        name: "La Croisette",
        description:
          "Hotels (Carlton, Martinez, Majestic) and private beaches.",
      },
      {
        name: "Nice Côte d'Azur airport",
        description: "Terminals 1 and 2, arrivals of talent and press.",
      },
      {
        name: "Cannes centre & station",
        description: "Access and accommodation for public and professionals.",
      },
      {
        name: "Mougins & hinterland",
        description: "Villas, dinners and parties outside the centre.",
      },
    ],
  },
  generic: {
    heading: "CANNESERIES from a chauffeur's seat",
    subheading:
      "In April, the Palais des Festivals rolls out its pink carpet for the international series festival.",
    paragraphs: [
      "Every spring, CANNESERIES takes over the Palais des Festivals in Cannes to celebrate series: screenings, competition and a pink carpet on the same steps as the great film ascent. A chauffeur who knows the authorised drop-offs gets you as close as possible without missing your screening.",
      "Talent, press and guests arrive mainly through Nice Côte d'Azur airport. With flight tracking and a name-board welcome, we absorb the April delays to reach your hotel without waiting.",
      "The festival chains screenings, panels and parties. Hourly hire keeps the same chauffeur through the day and secures the late-night returns after the parties.",
    ],
    bullets: [
      "Drop-off nearest the Palais",
      "Flight tracking in April",
      "Returns past midnight",
    ],
  },
  process: {
    heading: "Booking a chauffeur for CANNESERIES",
    steps: [
      "Tell us your festival dates, your hotel and your screening times.",
      "We plan the routes around the Palais perimeter and the pink-carpet crowds.",
      "The chauffeur arrives in uniform, with flight tracking for your Nice arrivals.",
      "Direct transfer or hourly hire to cover screenings, panels and parties.",
    ],
  },
  faq: [
    {
      q: "How do I book a private chauffeur for CANNESERIES?",
      a: "Book early: in April, Cannes hosts the series festival and demand rises. Share your dates, your hotel and your screening times for a dedicated chauffeur throughout.",
    },
    {
      q: "Can you drop me as close as possible to the pink carpet?",
      a: "Yes. During CANNESERIES, the approaches to the Palais des Festivals are busy. The chauffeur drops you at the nearest authorised point within the perimeter and timing, then collects you on the way out.",
    },
    {
      q: "Do you handle Nice airport arrivals?",
      a: "Yes, with flight tracking and a name-board welcome. Talent and press arrive through Nice Côte d'Azur; we adjust the pick-up time if a flight is delayed.",
    },
    {
      q: "Do you offer full-day hire?",
      a: "Yes. A dedicated chauffeur stays available to chain screenings, panels and professional meetings without worrying about parking in Cannes.",
    },
    {
      q: "Do you cover late returns after the parties?",
      a: "Yes. Evening screenings and festival parties end late: the chauffeur waits and guarantees your late-night return.",
    },
    {
      q: "Why choose KS Rent Services for CANNESERIES?",
      a: "Our private chauffeur service on the French Riviera is rated 5.0/5 from 38 Google reviews: a uniformed, punctual and discreet chauffeur used to the Palais des Festivals events.",
    },
  ],
  relatedLinks: [
    { label: "Private car service in Cannes", href: "/en/chauffeur/cannes" },
    { label: "Private car service in Nice", href: "/en/chauffeur/nice" },
    {
      label: "Private car service at Nice Airport",
      href: "/en/chauffeur/airport",
    },
    {
      label: "Rent a car Cannes",
      href: "/en/location/car-rental-cannes",
    },
  ],
  cta: {
    title: "Your chauffeur for CANNESERIES",
    text: "Airport welcome, pink carpet and parties: book a dedicated chauffeur for the series festival.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "CANNESERIES Chauffeur — series festival VIP car service",
    description:
      "Private chauffeur during CANNESERIES in Cannes: pink-carpet drop-off at the Palais des Festivals, Nice airport meet-and-greet, hourly hire and late-night runs.",
    canonical: "/en/chauffeur/canneseries",
  },
};

export default canneseriesEn;
